/**
 * Org-level custom ICD-10 library helpers.
 *
 * One entry shape serves two jobs:
 *   - a REMAP rule when `sourceCode` is set (e.g. E55 -> E55.9), and
 *   - a SEARCHABLE custom code (always, via `code` + `description`).
 *
 * These are pure functions: the same org data (auth.orgInfo.icd10Library) is
 * looked up here on the frontend and re-implemented on the backend — one source
 * of truth, two lookups. See docs/superpowers/specs/2026-06-08-icd10-custom-library-design.md
 */

import { v28Tier, drgSeverity } from "./codeOrdering/codeFacts";

/** Normalize an ICD-10 code for matching. Codes are case-insensitive but dot-significant. */
export function normalizeIcd10Code(code) {
  if (code == null) return "";
  return String(code).trim().toUpperCase();
}

/**
 * Classify a source -> target swap by clinical impact.
 * "routine": same 3-character condition family and no increase in risk weight —
 * code-set maintenance (descriptor updates, annual revisions, specificity within
 * the same condition).
 * "clinical": crosses condition families (I10 -> I11.0) OR raises the V28 risk
 * tier / MS-DRG severity (E11.9 -> E11.22) — the swap changes clinical meaning,
 * so it needs an explicit confirm at rule creation and a visible flag (with
 * one-click revert) on every note it touches.
 */
export function classifyIcd10SwapRisk(sourceCode, targetCode) {
  const src = normalizeIcd10Code(sourceCode);
  const tgt = normalizeIcd10Code(targetCode);
  if (!src || !tgt || src === tgt) return "routine";
  const crossFamily = src.split(".")[0].slice(0, 3) !== tgt.split(".")[0].slice(0, 3);
  const raisesRisk = v28Tier(tgt) > v28Tier(src) || drgSeverity(tgt) > drgSeverity(src);
  return crossFamily || raisesRisk ? "clinical" : "routine";
}

/**
 * Build a remap lookup: { [normalizedSourceCode]: { code, description, clinicalImpact? } }.
 * Only enabled entries WITH a non-empty sourceCode produce a rule.
 */
export function buildIcd10RemapMap(icd10Library) {
  const map = {};
  if (!Array.isArray(icd10Library)) return map;
  for (const entry of icd10Library) {
    if (!entry || entry.enabled === false) continue;
    const src = normalizeIcd10Code(entry.sourceCode);
    if (!src) continue;
    // A remap rule needs a non-empty TARGET code. A row with a sourceCode but a
    // blank/missing `code` is not a usable rule — applying it would overwrite a real
    // diagnosis code with `undefined`, which then surfaces as the literal "undefined"
    // in the AdvancedMD paste. Skip it so the source code is left untouched. Mirrors
    // the backend validator's `if (override != null && override.code)` guard.
    if (!normalizeIcd10Code(entry.code)) continue;
    // Last-wins on duplicate sourceCode. The management UI (ModalProfileICD10Library)
    // rejects a second entry with the same sourceCode, so duplicates shouldn't occur.
    map[src] = {
      code: entry.code,
      description: entry.description || "",
      ...(entry.clinicalImpact ? { clinicalImpact: true } : {}),
    };
  }
  return map;
}

/** True if any code in the list would be remapped (used to avoid an unnecessary persist). */
export function remapApplies(codes, remapMap) {
  if (!Array.isArray(codes) || !remapMap) return false;
  return codes.some((item) => item && item.code != null && remapMap[normalizeIcd10Code(item.code)]);
}

/**
 * Apply the remap to an array of code objects ([{ code, description, ... }]).
 * Returns a NEW array when something changed, otherwise the SAME reference
 * (so callers can cheaply skip persisting). Org description wins; if blank, the
 * original description is kept. All other fields (alternatives, modifier, ...) are preserved.
 *
 * Never silent: a swapped item carries `swappedFrom` (the original code pair)
 * so the note UI can flag the switch to the clinician and offer a one-click
 * revert, plus `swapClinicalImpact` when the rule changes clinical meaning.
 * Items the clinician explicitly reverted or re-picked carry `swapLocked` and
 * are never remapped again — the clinician's explicit choice outranks org policy.
 */
export function applyIcd10Remap(codes, remapMap) {
  if (!Array.isArray(codes) || !remapMap) return codes;
  let changed = false;
  const out = codes.map((item) => {
    if (!item || item.code == null || item.swapLocked) return item;
    const target = remapMap[normalizeIcd10Code(item.code)];
    if (!target) return item;
    changed = true;
    // The AI's per-code scores (specificity, v28 flag) describe the ORIGINAL
    // code; carrying them onto the swapped code paints contradictory chips
    // (e.g. "High specificity" on a 3-character category code). Drop them —
    // code-derived signals are recomputed from the new code string.
    const { specificity, v28, ...rest } = item;
    return {
      ...rest,
      code: target.code,
      description: target.description || item.description,
      swappedFrom: item.swappedFrom || { code: item.code, description: item.description || "" },
      ...(target.clinicalImpact ? { swapClinicalImpact: true } : {}),
    };
  });
  return changed ? out : codes;
}

/**
 * Merge org library entries into ICD-10 search results and suppress overridden source codes.
 * @param {Array<{n:string,d:string}>} apiResults - from api.getIcd10()
 * @param {Array} icd10Library - auth.orgInfo.icd10Library
 * @param {string} search - the current query
 * @returns {Array<{n:string,d:string,custom?:boolean}>} custom options first, source codes removed, deduped by code
 */
export function mergeIcd10SearchOptions(apiResults, icd10Library, search) {
  const results = Array.isArray(apiResults) ? apiResults : [];
  const lib = Array.isArray(icd10Library) ? icd10Library : [];
  const remapMap = buildIcd10RemapMap(lib);
  const q = (search || "").trim().toLowerCase();

  // 1) Org options matching the search.
  const customByCode = {};
  const customOptions = [];
  for (const entry of lib) {
    if (!entry || entry.enabled === false || !entry.code) continue;
    const desc = entry.description || "";
    if (q && !(entry.code + " " + desc).toLowerCase().includes(q)) continue;
    const norm = normalizeIcd10Code(entry.code);
    if (customByCode[norm]) continue;
    customByCode[norm] = true;
    // Emit the org's code verbatim (entry.code), not the normalized form: this value
    // is displayed and stored in the note, so it must preserve the supervisor's exact input.
    customOptions.push({ n: entry.code, d: desc, custom: true });
  }

  // 2) Standard results minus suppressed source codes and anything already shown as custom.
  const standard = [];
  for (const item of results) {
    if (!item || item.n == null) continue;
    const norm = normalizeIcd10Code(item.n);
    if (remapMap[norm]) continue; // suppress overridden source code
    if (customByCode[norm]) continue; // already shown as a custom option
    standard.push(item);
  }

  return [...customOptions, ...standard];
}

// ICD-10-CM chapter boundaries by 3-character category. Categories are
// letter+2-alphanumeric (e.g. E11, O9A), so plain string comparison orders them.
const ICD10_CHAPTERS = [
  ["A00", "B99", "infectious"],
  ["C00", "D49", "neoplasms"],
  ["D50", "D89", "blood"],
  ["E00", "E89", "endocrine"],
  ["F01", "F99", "mental"],
  ["G00", "G99", "nervous"],
  ["H00", "H59", "eye"],
  ["H60", "H95", "ear"],
  ["I00", "I99", "circulatory"],
  ["J00", "J99", "respiratory"],
  ["K00", "K95", "digestive"],
  ["L00", "L99", "skin"],
  ["M00", "M99", "musculoskeletal"],
  ["N00", "N99", "genitourinary"],
  ["O00", "O9A", "pregnancy"],
  ["P00", "P96", "perinatal"],
  ["Q00", "Q99", "congenital"],
  ["R00", "R99", "symptoms"],
  ["S00", "T88", "injury"],
  ["U00", "U85", "special"],
  ["V00", "Y99", "external"],
  ["Z00", "Z99", "zcodes"],
];

/**
 * Map an ICD-10 code to its chapter key (used for the library's group chips).
 * Returns "other" for anything that doesn't parse as an ICD-10-CM code.
 */
export function icd10ChapterKey(code) {
  const cat = normalizeIcd10Code(code).split(".")[0].slice(0, 3);
  if (!/^[A-Z][0-9A-Z]{1,2}$/.test(cat)) return "other";
  const padded = (cat + "0").slice(0, 3);
  for (const [lo, hi, key] of ICD10_CHAPTERS) {
    if (padded >= lo && padded <= hi) return key;
  }
  return "other";
}

/**
 * Build seed library rows from the org's used-code aggregation (api.getUsedIcd10Codes).
 * Each used code becomes an identity row: `code` set, `sourceCode` blank (no remap yet),
 * enabled, ready for a supervisor to refine into a remap. Deduped by normalized code;
 * input order is preserved (the aggregation is already ranked by frequency).
 * @param {Array<{code:string,description?:string}>} usedCodes
 * @param {string} [createdAt] ISO timestamp to stamp on each row (caller supplies; keeps this pure/testable)
 * @returns {Array} library entries
 */
export function seedRowsFromUsedCodes(usedCodes, createdAt) {
  const rows = [];
  if (!Array.isArray(usedCodes)) return rows;
  const seen = {};
  for (const u of usedCodes) {
    if (!u || !u.code) continue;
    const norm = normalizeIcd10Code(u.code);
    if (!norm || seen[norm]) continue;
    seen[norm] = true;
    rows.push({
      id: "seed-" + norm,
      sourceCode: "",
      code: u.code,
      description: u.description || "",
      enabled: true,
      createdAt: createdAt || "",
      updatedAt: createdAt || "",
    });
  }
  return rows;
}
